import { useState } from "react";
import { useLanguage, useT } from "@/context/LanguageContext";
import { translations } from "@/data/translations";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = ["features", "disciplines", "screenshots", "pricing", "faq"];

export default function Navbar() {
  const { lang, toggle } = useLanguage();
  const t = useT(translations.nav);
  const [open, setOpen] = useState(false);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 w-full z-50 bg-[#1a1f26]/80 backdrop-blur-md border-b border-white/5"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl flex items-center justify-between h-16">
        <a
          href="#hero"
          data-testid="navbar-logo"
          className="font-bold text-xl tracking-tight text-white"
          style={{ fontFamily: "Oswald, sans-serif" }}
        >
          MMA<span className="text-[#b87b56]">TIMER</span>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((key) => (
            <a
              key={key}
              href={`#${key}`}
              data-testid={`nav-link-${key}`}
              className="text-sm font-medium uppercase tracking-widest text-slate-400 hover:text-[#b87b56] transition-colors"
            >
              {t[key]}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="h-9 px-4 rounded-sm border border-white/10 bg-transparent hover:bg-[#39424F] text-sm font-bold uppercase tracking-wider text-white transition-all"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>

          <a
            href="https://apps.apple.com/us/app/boxing-mma-timer/id6760870249"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-download-btn"
            className="hidden md:inline-flex h-9 px-5 rounded-sm bg-[#b87b56] hover:bg-[#a66d4b] text-white text-sm font-bold uppercase tracking-wider items-center transition-all hover:translate-y-[-1px] hover:shadow-[0_4px_20px_rgba(184,123,86,0.4)]"
          >
            {t.download}
          </a>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#1a1f26]/95 backdrop-blur-md border-b border-white/5 overflow-hidden"
          >
            <div className="flex flex-col items-center gap-4 py-6">
              {navLinks.map((key) => (
                <a
                  key={key}
                  href={`#${key}`}
                  data-testid={`mobile-nav-link-${key}`}
                  className="text-sm font-medium uppercase tracking-widest text-slate-400 hover:text-[#b87b56] transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {t[key]}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
